'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiArrowRightLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { Link } from '@/i18n/navigation';
import { DarkVeil } from '@/components/dark-veil';
import { dur, ease, sceneItem, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * Kapanış CTA — sayfanın son içerik parçası, SSS ile footer'ın arasında.
 *
 * Buraya kadar okuyan kişi ikna olmuş sayılır; bölümün işi ona sonraki adımı
 * vermek.
 *
 * İki aksiyon var ama EŞİT DEĞİL (kullanıcı kararı, 9 Ağu 2026). Önceki sürüm
 * bilinçli olarak tek butonluydu; gerekçe hâlâ geçerli, o yüzden ikincisi
 * yarışan bir buton değil çerçeveli bir bağlantı: dolu marka yeşili yalnız
 * "Ücretsiz dene"de, "Planları Gör" ise şeridin kendi hattıyla aynı
 * mürekkepte. Yan yana iki DOLU buton olsaydı "hangisi?" sorusu doğar ve karar
 * gecikirdi.
 *
 * Fiyat bağlantısı hero'nun ikincil butonuyla aynı yere gidiyor (/pricing) ve
 * metnini de ondan okuyor — aynı bağlantının adı sitenin iki yerinde farklı
 * olmasın.
 *
 * ── Metin neden hero'yu tekrar etmiyor ────────────────────────────────────
 *
 * Hero bir VAAT söylüyor ("boşa giden bütçeye son"). Burası farklı bir an:
 * kullanıcı ürünün ne olduğunu ve kontrolün kendisinde kaldığını artık
 * biliyor (SSS'teki "control" maddesi bunu açıkça söylüyor). Kapanış o
 * halkayı kapatıyor — denemeye başlamak risksiz, çünkü ajan onay almadan
 * yayına bir şey çıkarmıyor. Aynı cümleyi ikinci kez duymak ise ikna etmez,
 * yalnızca sayfanın söyleyecek yeni şeyi kalmadığını gösterir.
 *
 * ── Koyu yüzey bir ŞERİT ──────────────────────────────────────────────────
 *
 * Kullanıcı kararları (9 Ağu 2026): footer beyaz, bu bölümün zemini tam
 * beyaz, köşe yarıçapı 0, dikey boşluk yok — şerit üstteki SSS'e ve alttaki
 * footer'a tam yapışık — ve genişliği TAM içerik sütunu kadar.
 *
 * Önceden CTA ile footer tek bir koyu gövdeydi (`.dark-close`) ve yeşil rampa
 * ikisinin toplam yüksekliğine yayılıyordu; o kap kaldırıldı. Işık şimdi
 * şeridin kendi içinde, yani ortada bölünecek tek bir yüzey kalmadı.
 *
 * Sayfanın sonu böylece iki parçaya ayrılıyor: koyu şerit son bir vurgu,
 * footer sessiz bir dizin.
 *
 * ── Dört hat şeridi çerçeveliyor ──────────────────────────────────────────
 *
 * Sayfanın ızgara dili 1px'lik hatlar: hero'nun .hero-container kenarları,
 * marka şeridinin çerçevesi, SSS'in yan hatları, footer'ın ayracı. Şerit
 * sütunun tamamını kapladığı için dikey hatlar tam onun kenarında; kullanıcı
 * isteğiyle üste ve alta da birer yatay hat eklendi. Dördü birlikte şeridi
 * çerçeveliyor ve renkleri #ffffff20 — koyu yüzeyde duran hattın rengi
 * (marka şeridiyle aynı), beyazdaki siyah %12'nin karşılığı.
 *
 * ── Zemin: karanlık örtü ──────────────────────────────────────────────────
 *
 * Şeridin arkasında React Bits "Dark Veil" (components/dark-veil.tsx),
 * hero'nun dalga alanıyla aynı kabuk kurallarına göre. Örtünün kendi rengi
 * atılıp yalnız parlaklığı bizim rampamıza eşleniyor — paletin dışında tek bir
 * ton kalmıyor (--aqua-green-900 → --aqua-green-500). Katman `screen` ile
 * karışıyor:
 * örtünün koyu yerleri şeridin kendi gradient'ini olduğu gibi bırakıyor,
 * yalnız aydınlık damarları ekleniyor. Metnin arkasında ayrıca maskeyle
 * sönüyor (globals.css → .cta-veil) — süs değil, ölçülen kontrast meselesi.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * Sıra: yüzey → çerçeve → içerik. Önce şerit açılıyor (hatlar beyaz zeminde
 * görünmez, bu yüzden çerçeve ONDAN SONRA çizilebilir), sonra dört hat
 * kenarları kapatıyor, en son içerik yerleşiyor.
 *
 * Bunlara ek olarak şeridin tam üst ve alt kenarında birer BÖLÜM AYIRACI var
 * (kullanıcı isteği): sayfanın standart ayraç mürekkebiyle, siyah %12 ve TAM
 * GENİŞLİK — sütunun dışına, ekranın iki kenarına kadar uzanıyorlar. Şeridin
 * üstünde beyaz hat (.cta-rule--t) onları eziyor, yan boşluklarda koyu hat
 * görünüyor: göz tek bir kesintisiz çizgi okuyor, zemine göre rengini
 * değiştiren (globals.css → .cta-divider).
 *
 * Yatay hatlar SOLDAN SAĞA, dikeyler YUKARIDAN AŞAĞI çiziliyor — footer'ın
 * ayracı ve sayfanın diğer dikey hatlarıyla aynı yön. İçerikte sıra göz
 * sırasıyla aynı: etiket, başlık, cümle, buton, koşullar. Buton dördüncü
 * sırada geliyor; sahnenin sonunda inen tek parlak öğe o.
 */

/* Sıra: üst ayraç → şerit → dört hat → alt ayraç. DOM sırası da bu; çerçeve
   şeritten sonra geliyor ki onun üstüne boyansın. */
const frameV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

/**
 * Hat çiziliyor. Yay DEĞİL tween — çizilen bir hattın fiziksel karşılığı yok;
 * spring'in sönümlü aşımı ucu hedefin altına taşırıp geri çeker, hat "sekmiş"
 * gibi durur. Marka şeridindeki .brands-rail-rule da aynı gerekçeyle süreyle
 * çiziliyor.
 *
 * Opaklık ayrı ve daha kısa: hat çizilirken zaten görünür olmalı, yoksa
 * ilerleyen uç soluk kalıyor.
 *
 * Yön CSS'teki `transform-origin`'den geliyor; buradaki iki varyant yalnızca
 * hangi eksenin ölçekleneceğinde ayrışıyor.
 */
const drawTransition = {
  duration: dur.slow,
  ease: ease.out,
  opacity: { duration: dur.fast, ease: ease.out },
};

/** Dikey hat: yukarıdan aşağı. */
const ruleV: Variants = {
  hidden: { scaleY: 0, opacity: 0 },
  show: { scaleY: 1, opacity: 1, transition: drawTransition },
};

/** Yatay hat: soldan sağa — footer'ın ayracıyla aynı yön. */
const edgeV: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  show: { scaleX: 1, opacity: 1, transition: drawTransition },
};

/**
 * Şeridin zemini. YALNIZCA opaklık — konum ya da ölçek yok.
 *
 * Denenip elenen sürüm yüzeyi 28px aşağıdan yay ile getiriyordu; şerit alttaki
 * footer'a ve üstteki SSS'e yapışık olduğu için o hareket iki kenarında da
 * yalancı bir beyaz aralık açıyor, üstteki bölümün üstüne biniyordu. Yapışık
 * bir yüzey kaymaz; açılır.
 *
 * Bu yüzden yay da yok: kayan bir cisim olmayınca yayın taklit edeceği bir
 * fizik kalmıyor (DESIGN.md §2 — "spring yalnızca fiziksel karşılığı olan
 * şeyler için"). Fiziksel hareketi içerik taşıyor, `sceneItem` ile.
 */
const panelV: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      duration: dur.base,
      ease: ease.out,
      delayChildren: 0.1,
      staggerChildren: 0.1,
    },
  },
};

/**
 * Örtünün nihai opaklığı. Katmanın gücünü belirleyen sayı BU — CSS'teki
 * `.cta-veil { opacity }` değil.
 *
 * Sebebi ısırdı, not düşülüyor: `veilV` satır içi `style="opacity: …"` yazıyor
 * ve satır içi stil sınıfı eziyor. Varyant 1'e gittiği sürece CSS'e ne
 * yazılırsa yazılsın örtü tam güçte kalıyor; maske dışındaki her ayar sessizce
 * yok sayılıyordu. CSS'teki değer artık yalnızca JS gelmeden önceki hâl
 * (Motion o anda `initial`ı da uygulamamış oluyor) — ikisi aynı sayı olmalı.
 *
 * 0.5 ölçümden geliyor: maskenin ortadaki %44'ü ile çarpınca metnin arkasında
 * efektif %22 kalıyor ve en parlak yeşil damarın üstünde koşullar satırı
 * 4.65:1 veriyor (globals.css → .cta-veil). Kenarlarda metin olmadığı için
 * orada tam %50 kalabiliyor.
 */
const VEIL_OPACITY = 0.5;

/**
 * Örtü. Şerit açıldıktan SONRA ve yavaşça geliyor — "yüzey açıldı, sonra
 * çalışmaya başladı." Tween, çünkü opaklığın fiziksel karşılığı yok; süre
 * `ambient`, çünkü bu bir olay değil ortam.
 */
const veilV: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: VEIL_OPACITY, transition: { duration: dur.ambient, ease: ease.out } },
};

const contentV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.06 } },
};

/**
 * Örtünün rampası — sönükten parlağa, ikisi de paletten.
 *
 * `--aqua-green-900` → `--aqua-green-500`. Üst uç başlıktaki vurgunun ve
 * butonun mürekkebiyle birebir aynı: zemin markayı tekrar ediyor, ona yakın
 * bir renk uydurmuyor. Ham örtünün kendi moru tümüyle atılıyor (bileşenin
 * baş notu).
 */
const VEIL_LOW = '#03696c';
const VEIL_HIGH = '#12e193';

export function Cta() {
  const t = useTranslations('cta');
  /* İkincil aksiyonun metni hero'dan: aynı hedefe giden aynı bağlantı, tek
     kaynaktan. Kopyalansaydı iki yazı zamanla ayrışırdı. */
  const tHero = useTranslations('hero');
  const uid = useId();

  return (
    /* Sahneyi bölümün KENDİSİ yönetiyor, .cta-frame değil: ayraçlar tam
       genişlikte, yani sütunun dışında. Motion varyantları React context ile
       aktığı için aradaki düz `div`'ler zinciri kırmıyor — .cta-panel ve
       hatlar hâlâ bu orkestrasyonun çocukları. */
    <motion.section
      className="cta"
      aria-labelledby={`${uid}-title`}
      variants={frameV}
      initial={false}
      animate="show"
    >
      {/* Üst bölüm ayıracı — ekranın bir kenarından diğerine. Şeritten ÖNCE
          çiziliyor: kapanış bloğu önce sayfadan ayrılıyor, sonra kendi yüzeyi
          açılıyor. */}
      <motion.span className="cta-divider cta-divider--t" variants={edgeV} aria-hidden="true" />

      {/* Sütun hero, marka şeridi ve SSS ile aynı: .grid-item-8fr kendi
          `grid-column: 2` kuralıyla yerleşiyor, boş kardeşe gerek yok. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="cta-frame">
            <motion.div className="cta-panel" variants={panelV}>
              {/* Örtü ayrı katman: şeridin kendi gradient'inden bağımsız
                  açılıyor (yukarıda veilV). Zemine gömülseydi yüzey onunla
                  birlikte belirir, "sonra çalışmaya başladı" anı olmazdı.
                  WebGL kurulamazsa canvas boş kalıyor ve şerit kendi
                  gradient'iyle duruyor — eksik bir şey görünmüyor. */}
              <motion.div className="cta-veil" variants={veilV} aria-hidden="true">
                <DarkVeil
                  className="cta-veil-canvas"
                  colorLow={VEIL_LOW}
                  colorHigh={VEIL_HIGH}
                  speed={0.35}
                  warpAmount={0.08}
                  scanlineIntensity={0}
                  noiseIntensity={0}
                  resolutionScale={0.7}
                />
              </motion.div>

              <motion.div className="cta-content" variants={contentV}>
                <motion.p className="cta-eyebrow" variants={sceneItem}>
                  {t('eyebrow')}
                </motion.p>

                <motion.h2 className="cta-title" id={`${uid}-title`} variants={sceneItem}>
                  {t.rich('title', { hl })}
                </motion.h2>

                <motion.p className="cta-lead" variants={sceneItem}>
                  {t('lead')}
                </motion.p>

                {/* Gerçek <a> (next-intl Link → next/link → anchor). Aksiyon
                    onClick'e bağlanmıyor: JS gelmeden de tıklanabilir, arama
                    motoru ve GEO tarafı bağlantıyı görebiliyor.

                    Sarmalayıcı div hareketi taşıyor, buton değil: giriş
                    animasyonunun transform'u ile :active'in scale'i aynı öğede
                    çakışıyor — biri diğerini eziyor. */}
                <motion.div className="cta-action" variants={sceneItem}>
                  <Link href="/register" className="cta-button">
                    {t('button')}
                    <RiArrowRightLine className="cta-button-arrow" size={20} aria-hidden="true" />
                  </Link>
                  <Link href="/pricing" className="cta-button cta-button--ghost">
                    {tHero('secondaryCta')}
                  </Link>
                </motion.div>

                <motion.p className="cta-note" variants={sceneItem}>
                  {t('note')}
                </motion.p>
              </motion.div>
            </motion.div>

            {/* Çerçeve. Mutlak konumlu span — SSS'te aynı hizadaki çizgiler
                border, ama border animasyonlanamıyor (ve genişlik/yükseklik
                animasyonu zaten yasak, DESIGN.md §1).

                Şeritten SONRA geliyorlar: hem boyama sırası doğru olsun
                (hat şeridin üstünde) hem de hareket sırası — beyaz zeminde
                açık renkli bir hat görünmez, çerçeve ancak koyu yüzey
                geldikten sonra çizilebilir. */}
            <motion.span className="cta-rule cta-rule--t" variants={edgeV} aria-hidden="true" />
            <motion.span className="cta-rule cta-rule--b" variants={edgeV} aria-hidden="true" />
            <motion.span className="cta-rule cta-rule--l" variants={ruleV} aria-hidden="true" />
            <motion.span className="cta-rule cta-rule--r" variants={ruleV} aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Alt bölüm ayıracı. Sahnenin son parçası — blok aşağıdan da kapanıyor. */}
      <motion.span className="cta-divider cta-divider--b" variants={edgeV} aria-hidden="true" />
    </motion.section>
  );
}
